import type { BillingPlan, EngineSubscription } from '../../services/billingApi';
import type { Entitlement } from '../../services/entitlementsApi';

/**
 * Upgrade-CTA eligibility rules, kept OUT of PlanSummaryCard.tsx/UpgradeModal.tsx
 * so they are testable — those components (or their callers) import the
 * billing/entitlements APIs, which pull in Firebase, which jsdom cannot
 * load (house rule: RTL leaf components only). Mirrors creditBatches.ts's
 * canOfferTopup pattern for the same reason.
 *
 * Deliberately does NOT reuse TIER_RANK from
 * frontend/src/components/auth/landing/planCardState.ts — that module is
 * dead code (never imported by any rendered component, only by its own
 * test) and stale on mechanism (its comment claims upgrade is "immediate
 * and prorated"; the engine's actual mechanism is credit-as-free-time with
 * a deferred first charge — see UpgradeModal's upgrade-mode docs). A local
 * constant here avoids wiring shipped code to that dead module.
 */
const TIER_RANK: Record<string, number> = { pro: 1, max: 2 };

/** Plans in `plans` that are a strictly-higher tier than `current.tier` —
 *  never the same tier (that's a renewal, not an upgrade) and never a lower
 *  one (downgrade is explicitly out of scope: the engine's /upgrade
 *  endpoint has no direction check, so the FRONTEND is what must never
 *  offer or send a same-or-lower-tier target). An unranked tier string
 *  (future tier not yet in TIER_RANK) is excluded rather than crashing.
 *
 *  Takes a bare `{tier}` rather than a full Entitlement so UpgradeModal
 *  (which only ever has the currentTier string from its `upgrade` prop, not
 *  a full Entitlement row) can reuse this exact function — an Entitlement
 *  satisfies this shape too, so canOfferUpgrade below passes one through
 *  unchanged. */
export function higherTierTargets(current: { tier: string }, plans: BillingPlan[]): BillingPlan[] {
  const currentRank = TIER_RANK[current.tier];
  if (currentRank === undefined) return [];
  return plans.filter((p) => (TIER_RANK[p.tier] ?? -1) > currentRank);
}

/** Gate for the "Upgrade" CTA — an active paid subscriber whose vertical
 *  catalog includes at least one strictly-higher tier than the one they
 *  hold. Narrower than the engine's own gate on purpose, same posture
 *  canOfferTopup takes: a trial or a payment-failed subscription already
 *  shows its own priority action (Subscribe / Fix payment) elsewhere on the
 *  card, so this never doubles up with those. */
export function canOfferUpgrade(args: {
  ent: Entitlement;
  sub: EngineSubscription | undefined;
  plans: BillingPlan[];
  billingConfigured: boolean;
}): boolean {
  const { ent, sub, plans, billingConfigured } = args;
  return (
    billingConfigured &&
    !!ent.vertical &&
    ent.status === 'active' &&
    sub?.status === 'active' &&
    higherTierTargets(ent, plans).length > 0
  );
}
